package com.kanati.admin.state

import kotlin.math.absoluteValue
import kotlin.js.Date

fun formatMoney(amount: Long): String {
    val dollars = amount / 100
    val cents = (amount % 100).absoluteValue.toString().padStart(2, '0')
    return "$$dollars.$cents"
}

fun parseMoneyInput(value: String): Long? {
    val normalized = value.trim()
        .removePrefix("$")
        .replace(",", "")

    if (normalized.isEmpty()) return null
    val parts = normalized.split('.')
    if (parts.size > 2) return null

    val dollars = when {
        parts[0].isEmpty() && parts.size == 2 -> 0L
        else -> parts[0].toLongOrNull() ?: return null
    }

    val cents = when {
        parts.size == 1 -> 0L
        parts[1].isEmpty() -> 0L
        parts[1].length == 1 -> "${parts[1]}0".toLongOrNull() ?: return null
        parts[1].length == 2 -> parts[1].toLongOrNull() ?: return null
        else -> return null
    }

    return (dollars * 100) + cents
}

fun formatTaxRateInput(basisPoints: Int): String {
    val percentWhole = basisPoints / 100
    val percentFraction = (basisPoints % 100).absoluteValue.toString().padStart(2, '0')
    return if (percentFraction == "00") {
        percentWhole.toString()
    } else {
        "$percentWhole.$percentFraction".trimEnd('0').trimEnd('.')
    }
}

fun formatTaxRatePercent(basisPoints: Int): String = "${formatTaxRateInput(basisPoints)}%"

fun parseTaxRateInputToBps(value: String): Int? {
    val normalized = value.trim()
        .removeSuffix("%")
        .replace(",", "")

    if (normalized.isEmpty()) return null
    val parts = normalized.split('.')
    if (parts.size > 2) return null

    val wholePercent = when {
        parts[0].isEmpty() && parts.size == 2 -> 0
        else -> parts[0].toIntOrNull() ?: return null
    }

    val fractionBps = when {
        parts.size == 1 -> 0
        parts[1].isEmpty() -> 0
        parts[1].length == 1 -> "${parts[1]}0".toIntOrNull() ?: return null
        parts[1].length == 2 -> parts[1].toIntOrNull() ?: return null
        else -> return null
    }

    return (wholePercent * 100) + fractionBps
}

fun requireDistrictId(districtId: String): String? = districtId.trim().takeIf(String::isNotEmpty)

fun browserTimeZoneLabel(): String =
    runCatching { js("Intl.DateTimeFormat().resolvedOptions().timeZone") as? String }
        .getOrNull()
        ?.takeIf(String::isNotBlank)
        ?: "your local time zone"

fun parseLocalDateAndTimeInputToIso(dateValue: String, timeValue: String): String? {
    val normalizedDate = dateValue.trim()
    val normalizedTime = timeValue.trim()
    if (normalizedDate.isEmpty() || normalizedTime.isEmpty()) return null

    val normalizedTimeWithSeconds = when (normalizedTime.count { it == ':' }) {
        1 -> "$normalizedTime:00"
        2 -> normalizedTime
        else -> return null
    }

    val date = Date("${normalizedDate}T$normalizedTimeWithSeconds")
    return if (date.getTime().isNaN()) {
        null
    } else {
        date.toISOString()
    }
}

fun parseLocalDateTimeInputToIso(value: String): String? {
    val normalized = value.trim()
    if (normalized.isEmpty()) return null
    return if ('T' in normalized) {
        parseLocalDateAndTimeInputToIso(
            dateValue = normalized.substringBefore('T'),
            timeValue = normalized.substringAfter('T'),
        )
    } else {
        val date = Date(normalized)
        if (date.getTime().isNaN()) {
            null
        } else {
            date.toISOString()
        }
    }
}

fun formatLocalDateInputFromIso(value: String): String {
    val normalized = value.trim()
    if (normalized.isEmpty()) return ""
    val date = Date(normalized)
    if (date.getTime().isNaN()) return ""
    return buildString {
        append(date.getFullYear())
        append("-")
        append((date.getMonth() + 1).toString().padStart(2, '0'))
        append("-")
        append(date.getDate().toString().padStart(2, '0'))
    }
}

fun formatLocalTimeInputFromIso(value: String): String {
    val normalized = value.trim()
    if (normalized.isEmpty()) return ""
    val date = Date(normalized)
    if (date.getTime().isNaN()) return ""
    return buildString {
        append(date.getHours().toString().padStart(2, '0'))
        append(":")
        append(date.getMinutes().toString().padStart(2, '0'))
    }
}

fun formatLocalDateTimeInputFromIso(value: String): String {
    val localDate = formatLocalDateInputFromIso(value)
    val localTime = formatLocalTimeInputFromIso(value)
    return if (localDate.isNotEmpty() && localTime.isNotEmpty()) {
        "${localDate}T$localTime"
    } else {
        ""
    }
}

fun formatLocalDateTimeDisplay(value: String): String {
    val normalized = value.trim()
    if (normalized.isEmpty()) return value
    val date = Date(normalized)
    if (date.getTime().isNaN()) return value

    val month = listOf(
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    )[date.getMonth()]
    val day = date.getDate()
    val year = date.getFullYear()
    val hour24 = date.getHours()
    val minute = date.getMinutes().toString().padStart(2, '0')
    val meridiem = if (hour24 >= 12) "PM" else "AM"
    val hour12 = when (val normalizedHour = hour24 % 12) {
        0 -> 12
        else -> normalizedHour
    }

    return "$month $day, $year $hour12:$minute $meridiem"
}
